import { getMic } from "@/lib/mic";
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Mic, Square, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";

// After the Space: the coach asks aloud how he feels; he answers by voice; it's saved to his memory.
export default function ImmersionCheckIn({ iid, topicId, coach, ask, onReturn }) {
  const [state, setState] = useState("idle"); // idle | rec | saving | done
  const [ack, setAck] = useState("");
  const [milestone, setMilestone] = useState(null);
  const mr = useRef(null), chunks = useRef([]), asked = useRef(false);
  useEffect(() => { if (!asked.current) { asked.current = true; ask?.(); } }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const record = async () => {
    if (state === "rec") { mr.current?.stop(); return; }
    try {
      const stream = await getMic();
      const m = new MediaRecorder(stream); mr.current = m; chunks.current = [];
      m.ondataavailable = (e) => chunks.current.push(e.data);
      m.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setState("saving");
        try {
          const type = m.mimeType || "audio/webm";
          const fd = new FormData(); fd.append("file", new Blob(chunks.current, { type }), type.includes("mp4") ? "a.mp4" : "a.webm");
          const { data } = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (!data.text?.trim()) throw new Error("empty");
          const r = await api.post(`/immersions/${iid}/checkin`, { text: data.text.trim(), topic: topicId, local_date: new Date().toLocaleDateString("en-CA") });
          setAck(r.data.ack); setMilestone(r.data.milestone); setState("done");
        } catch { toast.error("Couldn't quite hear that. Try once more?"); setState("idle"); }
      };
      m.start(); setState("rec");
    } catch { }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 2 }} className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" data-testid="immersion-checkin">
      {state === "done" ? (
        <>
          <p className="font-serif text-2xl sm:text-3xl text-white mb-9 max-w-lg leading-snug" data-testid="immersion-checkin-ack">{ack}</p>
          {milestone && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 1.4 }}
              className="mb-9 max-w-sm rounded-2xl border border-white/15 bg-white/5 backdrop-blur-md px-6 py-5" data-testid="immersion-milestone">
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-2">This week · {milestone.count} immersions</p>
              <p className="font-serif text-lg text-white/90 leading-snug">{milestone.line}</p>
              <p className="text-xs text-white/40 mt-2">— {milestone.coach}</p>
            </motion.div>
          )}
        </>
      ) : (
        <>
          <p className="text-xs uppercase tracking-[0.25em] text-white/50 mb-3">{coach || "Your coach"} asks</p>
          <p className="font-serif text-3xl sm:text-4xl text-white mb-9 max-w-lg">How do you feel, right now?</p>
          <button onClick={record} disabled={state === "saving"} data-testid="immersion-checkin-mic"
            className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 transition-[background-color,transform] duration-300 ${state === "rec" ? "bg-white text-black scale-110 animate-pulse" : "bg-white/10 text-white hover:bg-white/20"}`}>
            {state === "saving" ? <Loader2 className="w-6 h-6 animate-spin" /> : state === "rec" ? <Square className="w-6 h-6" /> : <Mic className="w-7 h-7" />}
          </button>
          <p className="text-sm text-white/50 mb-9" data-testid="immersion-checkin-hint">{state === "rec" ? "Listening… tap to finish" : state === "saving" ? "Hearing you…" : "Tap and say it in a few words"}</p>
        </>
      )}
      <Button size="lg" variant={state === "done" ? "secondary" : "ghost"} className="rounded-full px-8 text-white/80" onClick={onReturn} data-testid="immersion-return-btn">
        {state === "done" ? "Return" : "Skip and return"}
      </Button>
    </motion.div>
  );
}
